import React from 'react';
import styled from 'styled-components';
import {
  TextField as BaseTextField,
  TextFieldProps as BaseTextFieldProps,
} from '@mui/material';
import { isNumeric } from '../../utils/common';
import { ComponentSize, COMPONENT_SIZE_MAP } from '../../types/types';
import { componentToken, declaration } from '../../style/utils';

export type TextFieldProps = Omit<BaseTextFieldProps, 'size'> & {
  size?: ComponentSize;
  numeric?: boolean;
  readOnly?: boolean;
};

type TextFieldStyledProps = Omit<BaseTextFieldProps, 'size'> & {
  $size?: ComponentSize;
  $readOnly?: boolean;
};

const TextFieldStyled = styled(BaseTextField)<TextFieldStyledProps>`
  .MuiInputBase-root {
    height: ${({ $size, multiline }: TextFieldStyledProps) =>
      multiline ? 'fit-content' : `${COMPONENT_SIZE_MAP[$size || 'medium']}px`};
    min-height: ${({ $size }: TextFieldStyledProps) =>
      `${COMPONENT_SIZE_MAP[$size || 'medium']}px`};
    font-size: ${componentToken('textField.input.fontSize')};
    border-radius: ${componentToken('textField.input.radius')};
    padding-top: ${({ multiline }) => (multiline ? 10 : 0)}px;
    padding-bottom: ${({ multiline }) => (multiline ? 10 : 0)}px;
    padding-right: ${({ multiline }) => (multiline ? '0px' : undefined)};
    align-items: ${({ multiline }) => (multiline ? 'flex-start' : undefined)};

    &.Mui-disabled {
      background-color: ${componentToken('textField.input.disabledBackground')};
      border: none;
      color: ${componentToken('textField.input.disabledColor')};
      .MuiOutlinedInput-notchedOutline {
        border: none !important;
      }
    }
  }

  .MuiOutlinedInput-root {
    border: 1px solid ${componentToken('textField.input.border')};
    ${({ theme, error }) => {
      const input = theme.tokens.components.textField.input;
      return `
        border: none;
        ${error ? declaration('background-color', input.errorBackground) : ''}
        .MuiOutlinedInput-notchedOutline {
          border: 1px solid ${
            error ? input.errorBorder : input.border
          } !important;
          border-radius: ${input.outlineRadius};
          transition: border 0.2s !important;
        };
        &.Mui-focused {
          border: none !important;
          ${
            error
              ? declaration('background-color', input.errorFocusedBackground)
              : ''
          }
          box-shadow: ${error ? input.errorFocusShadow : input.focusShadow};
          .MuiOutlinedInput-notchedOutline {
            border: 1px solid ${
              error ? input.errorFocusedOutline : input.focusedOutline
            } !important;
          };
        };
        :hover {
          .MuiOutlinedInput-notchedOutline {
            border: 1px solid ${
              error ? input.errorHoverOutline : input.hoverOutline
            } !important;
          }
        }
        ${
          error
            ? ''
            : `&:hover:not(.Mui-focused):not(.Mui-disabled) {
                box-shadow: ${input.hoverShadow};
              }`
        }
        &.Mui-disabled {
          .MuiOutlinedInput-notchedOutline {
            border: ${input.disabledOutline} !important;
          }
          .MuiInputBase-input {
            ${declaration('-webkit-text-fill-color', input.disabledTextFill)}
            &::placeholder {
              ${declaration('color', input.disabledPlaceholder)}
              ${declaration(
                '-webkit-text-fill-color',
                input.disabledPlaceholder,
              )}
            }
          }
        }
      `;
    }};
  }

  .MuiOutlinedInput-root.Mui-focused {
    border: 1px solid ${componentToken('textField.input.focusedBorder')};
  }

  .MuiOutlinedInput-notchedOutline {
    border: none !important;
    border-width: 0px !important;
  }

  & .MuiInputLabel-root {
    top: ${({ $size }: any) => ($size === 'large' ? '-6px' : '-11px')};
  }

  & .MuiInputLabel-shrink {
    top: 1px;
    font-size: ${componentToken('textField.input.labelShrinkFontSize')};
  }

  .MuiInputBase-input {
    &::placeholder {
      color: ${componentToken('textField.input.placeholderColor')};
      opacity: 1;
    }
  }

  .MuiFormHelperText-root {
    margin-left: 0;
  }

  pointer-events: ${({ $readOnly }) => ($readOnly ? 'none' : 'auto')};

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    box-shadow: ${componentToken('textField.input.autofillShadow')} !important;
    -webkit-box-shadow: ${componentToken(
      'textField.input.autofillShadow',
    )} !important;
  }
`;

const TextField = React.forwardRef(
  ({ size, numeric, readOnly, ...props }: TextFieldProps, ref: any) => {
    const isValidKey = (e: any) => {
      if (!numeric) return true;

      return (
        isNumeric(e.key) ||
        (e.metaKey && e.key.toLowerCase() === 'a') ||
        e.key === 'Backspace' ||
        e.key.toLowerCase().includes('arrow')
      );
    };

    const handleKeyDown = (e: any) => {
      if (!isValidKey(e) || readOnly) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      props?.onKeyDown?.(e);
    };

    return (
      <TextFieldStyled
        ref={ref}
        $size={size}
        $readOnly={readOnly}
        {...props}
        onKeyDown={handleKeyDown}
      />
    );
  },
);

export default TextField;
